import React from 'react';
import { Box, Link, Stack, Typography, styled } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { Button } from 'components/Button/Button';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import type { BenefitDetail } from 'features/resources/types';

const ContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflowY: 'auto',
});

const ImageBanner = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  flexShrink: 0,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
}));

const Body = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  justifyContent: 'space-between',
  gap: theme.spacing(3.5),
  paddingTop: theme.spacing(5.75),
  paddingBottom: theme.spacing(5),
  paddingInline: theme.spacing(6.25),
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(7.5),
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(3.5),
    paddingInline: theme.spacing(4),
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.brandShade2.main,
  color: theme.palette.common.white,
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'center',
  paddingTop: theme.spacing(3),
  [theme.breakpoints.between('sm', 'md')]: {
    paddingBottom: theme.spacing(4.25),
  },
}));

export interface BenefitDetailContentProps {
  benefit?: BenefitDetail;
  onClickVisit?: () => void;
}

export const BenefitDetailContent = ({ benefit, onClickVisit }: BenefitDetailContentProps) => {
  const { translation } = useTranslationContext();

  if (!benefit) {
    return null;
  }

  return (
    <ContentContainer>
      {benefit.imageUrl && (
        <ImageBanner>
          <img src={benefit.imageUrl} alt={translation('benefits_image')} />
        </ImageBanner>
      )}
      <Body>
        <Stack spacing={2} sx={{ maxWidth: 506, width: '100%', alignSelf: 'center' }}>
          <Typography fontWeight={500} variant="title2" className="benefit-detail-page__title">
            {benefit.title}
          </Typography>
          <Typography
            variant="callout"
            className="benefit-detail-page__description"
            sx={{ whiteSpace: 'pre-line' }}
          >
            {benefit.description}
          </Typography>
          {benefit.phoneNumber && (
            <Typography variant="callout" className="benefit-detail-page__phone">
              {`${translation('phone')}: `}
              <Link href={`tel:${benefit.phoneNumber.replace(/\s/g, '')}`} underline="always">
                {benefit.phoneNumber}
              </Link>
            </Typography>
          )}
        </Stack>
        {benefit.visitUrl && (
          <ButtonContainer>
            <StyledButton
              size="xlarge"
              fullWidth
              className="benefit-detail-page__visit-button"
              onClick={onClickVisit}
              endIcon={<OpenInNew sx={{ fontSize: 16 }} />}
            >
              {translation('resources_member_benefit_detail_visit_button_title')}
            </StyledButton>
          </ButtonContainer>
        )}
      </Body>
    </ContentContainer>
  );
};
